import React from 'react';
import {useMatches} from '@remix-run/react';

//components
import Container from 'components/Container';
import SanityImage from 'components/SanityImage';
import PortableText from 'components/PortableText';
import {Link} from 'components/Link';

//domains
import {LINK_INTERNAL} from 'domains/global';
import {withHongKongDialCode} from '~/lib/utils';

export default function Footer() {
  const [root] = useMatches();
  const footer = root?.data?.footer as any;
  const isEn =
    (root?.data?.selectedLocale as {language?: string} | undefined)
      ?.language === 'EN';

  return (
    <footer
      className="overflow-hidden bg-hk-deep-blue pb-8 pt-14 text-white"
      role="contentinfo"
      id="footer"
    >
      <Container>
        <div className="mb-10 grid gap-12 lg:grid-cols-3 lg:gap-4 lg:gap-y-12">
          {footer.footer_links &&
            footer?.footer_links?.map((l: any) => {
              if (l._type === 'custom.footerLinkGroup')
                return (
                  <div key={l._key} className="lg:col-span-1">
                    <h3 className="mb-6 inline-block border-b-2 border-hk-orange pb-2 font-bold uppercase tracking-wider">
                      {l.title}
                    </h3>
                    <ul className="grid gap-3.5 text-slate-300">
                      {l.links &&
                        l.links?.map((link: any) => {
                          if (link._type === 'custom.linkWithIcon') {
                            return (
                              <li key={link._key} className="font-normal">
                                <a
                                  href={withHongKongDialCode(link.url)}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  className="flex items-center space-x-2 transition-colors duration-200 hover:text-hk-orange"
                                >
                                  <div className="w-[22px]">
                                    <SanityImage
                                      layout="responsive"
                                      sizes={['50vw, 100vw']}
                                      src={link.icon?.asset._ref}
                                      alt={
                                        (link as {title?: string})?.title ?? ''
                                      }
                                      loading="lazy"
                                    />
                                  </div>
                                  <span className="">{link?.icon_title}</span>
                                </a>
                              </li>
                            );
                          }
                          if (link._type === 'block') {
                            return (
                              <li key={link._key} className="font-normal">
                                <PortableText blocks={link} />
                              </li>
                            );
                          }
                          if (link._type === 'linkInternal') {
                            // Site-account login removed — membership lives
                            // in the Marsello customer portal now, so hide
                            // the CMS "My Account" item.
                            if (
                              link._key === 'ceb49658be2e' ||
                              link.title === 'My Account' ||
                              link.title === '我的帳戶'
                            ) {
                              return null;
                            }
                            const slug = generateSanityInternalPath({
                              type: link?.reference?._type,
                              slug: link?.reference?.slug?.current,
                            });
                            const internal_link_slug =
                              slug === '/blogs/all' ? '/blogs' : slug;
                            return (
                              <li key={link._key} className="font-normal">
                                <Link
                                  to={`${internal_link_slug}`}
                                  prefetch="intent"
                                  className="transition-colors duration-200 hover:text-hk-orange"
                                >
                                  {link.title}
                                </Link>
                              </li>
                            );
                          }
                          return (
                            <li key={link._key} className="font-normal">
                              <a
                                href={link.url}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="transition-colors duration-200 hover:text-hk-orange"
                              >
                                {link.title}
                              </a>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                );
              return;
            })}
        </div>
        <div className="mb-12 grid gap-12 lg:grid-cols-3 lg:gap-4 lg:gap-y-12">
          <div className="flex space-x-2 lg:col-span-1 lg:col-start-3">
            {footer.social_links &&
              footer.social_links.map((l: any) => {
                if (l.type === 'fb-like') {
                  return (
                    <div key={l._key} className="h-[36px]">
                      <iframe src="https://www.facebook.com/v2.5/plugins/like.php?action=like&app_id=&channel=https%3A%2F%2Fstaticxx.facebook.com%2Fx%2Fconnect%2Fxd_arbiter%2F%3Fversion%3D46%23cb%3Df3b1446bedf85ac%26domain%3Dhelmetking.com%26is_canvas%3Dfalse%26origin%3Dhttps%253A%252F%252Fhelmetking.com%252Ff2943e5a9ec7c8%26relation%3Dparent.parent&container_width=0&href=https%3A%2F%2Fwww.facebook.com%2Fjpmotobike%2F&layout=button_count&locale=en_US&sdk=joey&show_faces=true"></iframe>
                    </div>
                  );
                }
                return (
                  <a
                    key={l._key}
                    title={l.title}
                    href={l.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-block w-[36px] flex-shrink-0 transition-transform duration-200 hover:-translate-y-0.5 hover:opacity-90"
                  >
                    <div className="">
                      <SanityImage
                        layout="responsive"
                        sizes={['50vw, 100vw']}
                        src={l.icon?.asset._ref}
                        alt={(l as {title?: string})?.title ?? ''}
                        loading="lazy"
                      />
                    </div>
                  </a>
                );
              })}
          </div>
        </div>
        {/* Code-owned routes that have no Sanity document, so they can never
            appear in the CMS-driven link groups above. Without a link from
            somewhere in the site chrome these pages are orphaned — nothing
            crawls to them, and the FAQ's structured data earns nothing. */}
        <nav
          aria-label={isEn ? 'Help' : '幫助'}
          className="mb-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-normal text-slate-300"
        >
          <Link
            to="/pages/faq"
            prefetch="intent"
            className="transition-colors duration-200 hover:text-hk-orange"
          >
            {isEn ? 'FAQ' : '常見問題'}
          </Link>
          <Link
            to="/store-locator"
            prefetch="intent"
            className="transition-colors duration-200 hover:text-hk-orange"
          >
            {isEn ? 'Store Locator' : '門市地址'}
          </Link>
        </nav>
        <div className="grid items-center gap-4 border-t border-white/10 pt-6 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <div className="text-sm font-normal text-slate-400">
              {footer.copyright_text}
            </div>
          </div>
          <div className="lg:col-span-1">
            <SanityImage
              layout="responsive"
              sizes={['50vw, 100vw']}
              src={footer.payment_icon?.asset._ref}
              alt="Visa, Mastercard, American Express, Apple Pay, Google Pay, Shop Pay, PayMe, AlipayHK, WeChat Pay"
              loading="lazy"
            />
          </div>
        </div>
      </Container>
    </footer>
  );
}
export const generateSanityInternalPath = ({
  type,
  slug,
}: {
  type: string;
  slug: string;
}) => {
  if (type === 'page') {
    return `/pages/${slug}`;
  }
  if (type === 'category') {
    return `/blogs/${slug}`;
  }
  if (type === 'collection') {
    return `/collections/${slug}`;
  }
  if (type === 'product') {
    return `/products/${slug}`;
  }
  if (type === 'home') {
    return `/`;
  }
  return '/';
};
